import { SnackbarItem } from '@george-labs.com/design-system';

import type { Account } from '@shared/products-types';
import type { ProductWithHelpers } from '@shared/products-core';
import type { CopyProductNumberAction } from '@retail/overview-utils';
import { getActionsDropdownConfig } from '@shared/products-core';
import { t } from '@luna/i18n';
import { copyText } from '@luna/utils';
import { unsafeNavigate } from '@luna/router';
import { getFeatureConfig } from '@luna/feature-config';
import { addSnackbarItem } from '@shared/snackbar';
import { getTitleAlignedWithGeorgeApp } from '@shared/products-general-service';
import { trackEvent2 } from '@shared/statistics';
import { formatIBAN } from '@shared/formatting-utils';

import type { DropdownAction } from './types';
import { getProductNumber } from '../services/productNumberService';
import { isInsurance } from '../services/typeService';
import { isProductPapiCustomizationEnabled } from '../services/isProductPapiCustomizationEnabled';
import { changeProductVisibility } from './changeProductVisibility';
import { ActionsDropdown as ExternalPartialMigrationActionsDropdown } from '../external-partial-papi-migration/overview-actions-dropdown/ActionsDropdown';
import { ActionsDropdown as PartialMigrationActionsDropdown } from '../partial-papi-migration/overview-actions-dropdown/ActionsDropdown';
import { ActionsDropdown as LegacyActionsDropdown } from '../legacy/overview-actions-dropdown/ActionsDropdown';

const createCopyContractNumberActionItem = (product: ProductWithHelpers): DropdownAction | null => {
    if (!isInsurance(product) || !getProductNumber(product)) {
        return null;
    }

    return {
        type: 'COPY_CONTRACT_NUMBER',
        label: t('account.details.copyContractNumber'),
        'data-cy': 'overview-card-action-copy-contract-number',
        action: (showNotification) => {
            copyText(getProductNumber(product));
            showNotification?.(t('account.details.copyContractNumberSuccess'));
        },
    };
};

function getStaticDropdownActions(
    product: ProductWithHelpers,
    isAliasChangeAllowed: boolean,
): DropdownAction[] {
    const dropdownItems: DropdownAction[] = [
        {
            type: 'CHANGE_COLOR',
            label: t('mainOverview.changeColor'),
            'data-cy': 'overview-card-action-change-color',
        },
        {
            type: 'CHANGE_IMAGE',
            label: t('mainOverview.changeImage'),
            'data-cy': 'overview-card-action-change-image',
        },
        {
            type: 'HIDE_BOX',
            label: t('mainOverview.hideBox'),
            'data-cy': 'overview-card-action-hide',
            action: () => {
                changeProductVisibility({
                    hidden: true,
                    product,
                    trackEvent2Trigger: 'dashboard',
                });
                addSnackbarItem({
                    type: SnackbarItem.VARIANT.SUCCESS,
                    message: t('mainOverview.action.hideSuccess', {
                        product: getTitleAlignedWithGeorgeApp(product),
                    }),
                    'data-cy': 'overview-card-action-hide',
                    buttonProps: {
                        child: t('mainOverview.action.undo'),
                        onClick: () =>
                            changeProductVisibility({
                                hidden: false,
                                product,
                                trackEvent2Trigger: 'snackbar',
                            }),
                        'data-cy': 'overview-card-action-undo-hide',
                    },
                });
            },
        },
    ];

    isAliasChangeAllowed &&
        dropdownItems.unshift({
            type: 'CHANGE_NAME',
            label: t('mainOverview.changeName'),
            'data-cy': 'overview-card-action-change-name',
        });

    return dropdownItems;
}

export function getDropdownActions(
    product: ProductWithHelpers,
    isAliasChangeAllowed: boolean = true,
): DropdownAction[] {
    const staticDropdownActions = getStaticDropdownActions(product, isAliasChangeAllowed);

    return [createCopyContractNumberActionItem(product), ...staticDropdownActions].filter(
        (action) => !!action,
    ) as DropdownAction[];
}

export function getRegisteredDropdownActions(
    product: ProductWithHelpers<Account>,
    isAliasChangeAllowed: boolean,
    copyProductNumberAction?: CopyProductNumberAction,
    settingsUrl?: string,
): DropdownAction[] {
    const dropdownItems = getStaticDropdownActions(product, isAliasChangeAllowed);

    if (settingsUrl) {
        dropdownItems.unshift({
            type: 'SETTINGS',
            label: t('mainOverview.settings'),
            'data-cy': 'overview-card-action-settings',
            action: () => unsafeNavigate(settingsUrl),
        });
    }

    const productNumber = getProductNumber(product);
    const isProductNumberAnIban = productNumber === formatIBAN(product?.accountno?.iban);

    if (copyProductNumberAction && productNumber) {
        dropdownItems.unshift({
            type: 'COPY_PRODUCT_NUMBER',
            label: t(copyProductNumberAction.actionLabel),
            'data-cy': copyProductNumberAction['data-cy'],
            action: (showNotification) => {
                copyText(productNumber);
                showNotification?.(t(copyProductNumberAction.snackbarText));
                if (isProductNumberAnIban) {
                    trackEvent2('accountInfoCopy', 'ibanCopied', {
                        trigger: 'overview',
                    });
                }
            },
        });
    }

    return dropdownItems;
}

type ActionsDropdownComponent =
    | typeof ExternalPartialMigrationActionsDropdown
    | typeof PartialMigrationActionsDropdown
    | typeof LegacyActionsDropdown;

export const getActionsDropdownComponent = (
    account: ProductWithHelpers<Account>,
): ActionsDropdownComponent => {
    const actionsDropdownConfig = getActionsDropdownConfig(account.registerType);
    const isPAPICustomizationEnabled = isProductPapiCustomizationEnabled(account.registerType);

    if (actionsDropdownConfig && isPAPICustomizationEnabled && account.underlyingData) {
        return ExternalPartialMigrationActionsDropdown;
    }

    if (actionsDropdownConfig && !isPAPICustomizationEnabled) {
        return LegacyActionsDropdown;
    }

    const { relyOnGAPIForAggregation, relyOnPAPIEndpointsForData } = getFeatureConfig(
        ({ products }) => products,
    );
    const isPAPIDataLayerWithGAPIAggregation =
        relyOnPAPIEndpointsForData && relyOnGAPIForAggregation;

    if (isPAPICustomizationEnabled || isPAPIDataLayerWithGAPIAggregation) {
        return PartialMigrationActionsDropdown;
    }

    return LegacyActionsDropdown;
};
